/**
 * AgentEscalationQueuePage
 *
 * Portfolio-wide queue of agent-raised escalations awaiting a landlord answer
 * (BR-AGENT-ESC-1, BR-ESC-AUTHZ). A landlord answers each escalation here — dismiss is
 * REMOVED (BR-ESC-RESOLVE), answering is the only resolution path. Answering does NOT
 * touch agent-suspension state (BR-ESC-CONTINUE) — the agent runtime itself suspends and
 * resumes on escalation via a timed wake (run grant); only an explicit landlord takeover
 * pauses a case, surfaced per-case on the Lead's Overview tab / Repair Detail rather than
 * here.
 *
 * Route: /agent/escalations
 *
 * Pattern: modelled on AgentResponseQueuePage.tsx (same list/aging/resolve shape),
 * kept as a SEPARATE page — this run's slice scope does not merge it with the
 * response-review queue.
 *
 * Response types are derived from the boundary contract in `@resida/api-types/agent`
 * — the same Zod schemas apps/api registers on these routes — so any field-name drift
 * is a typecheck error rather than a runtime `undefined`.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MessageCircleQuestion } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { AgentUpgradePrompt } from '../components/billing/AgentUpgradePrompt';
import { PageHeader, EmptyState } from '../components/layout/index';
import { useBillingFeatures } from '../hooks/useBilling';
import { useDomainEvents } from '../hooks/useDomainEvents';
import { useUnseenCounts } from '../hooks/useAwareness';
import { fetchEscalationQueue, answerEscalation } from '../api/agent-escalations.api';
import type { EscalationListItem } from '@resida/api-types/agent';
import { WhyAgentStoppedNote } from '../components/agent/WhyAgentStoppedNote';
import { agentNameForSlug } from '../lib/playbook';
import { referenceCheckLeadPath } from '../lib/reference-check-links';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(createdAt: string): string {
  const ageMs = Date.now() - new Date(createdAt).getTime();
  const totalSeconds = Math.floor(ageMs / 1000);
  if (totalSeconds < 60) return 'just now';
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) return `${totalMinutes}m ago`;
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 24) return `${totalHours}h ago`;
  const totalDays = Math.floor(totalHours / 24);
  return `${totalDays}d ago`;
}

function getAgeBadgeVariant(createdAt: string): 'default' | 'secondary' | 'destructive' {
  const hours = (Date.now() - new Date(createdAt).getTime()) / 3_600_000;
  if (hours >= 24) return 'destructive';
  if (hours >= 4) return 'default';
  return 'secondary';
}

// ---------------------------------------------------------------------------
// Answer editor (per-item inline)
// ---------------------------------------------------------------------------

interface AnswerEditorProps {
  item: EscalationListItem;
  onResolved: () => void;
  onCancel: () => void;
}

function AnswerEditor({ item, onResolved, onCancel }: AnswerEditorProps) {
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!answer.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const result = await answerEscalation(item.id, answer.trim());
      if (result.status !== 'answered') {
        // Lost race: the case closed (BR-CASE-CLOSE-CLEANUP) and the escalation was closed out
        // as 'dismissed' — this answer was NOT recorded. Keep the editor open and say so; the
        // row is removed on the next queue load.
        setError('This escalation was already resolved — the case was closed, so your answer was not sent.');
        return;
      }
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send answer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="mt-4 space-y-2"
      data-testid={`escalation-answer-editor-${item.id}`}
    >
      <label
        htmlFor={`escalation-answer-content-${item.id}`}
        className="block text-xs font-medium text-muted-foreground"
      >
        Your answer to this escalation
      </label>
      <Textarea
        id={`escalation-answer-content-${item.id}`}
        data-testid={`escalation-answer-input-${item.id}`}
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        rows={4}
        maxLength={4000}
        placeholder="Write your answer here…"
        className="resize-y"
        aria-label="Answer to the agent's escalation question"
        required
      />
      <p className="text-xs text-muted-foreground text-right">
        {answer.length}/4000
      </p>
      {error && (
        <div
          role="alert"
          className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
        >
          {error}
        </div>
      )}
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={saving || !answer.trim()}
          data-testid={`escalation-answer-submit-${item.id}`}
          aria-label="Send answer for this escalation"
        >
          {saving ? 'Sending…' : 'Send answer'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={saving}
          data-testid={`escalation-answer-cancel-${item.id}`}
          aria-label="Cancel answering"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Escalation row
// ---------------------------------------------------------------------------

interface EscalationRowProps {
  item: EscalationListItem;
  onResolved: (id: string) => void;
  /** DC-4/BR-16 — un-cleared awareness item count for THIS row's lead/repair instance (0 = no
   *  mark). */
  unseenCount: number;
  /** Registers this row's Answer button so the `?escalation=<id>` deep link (reminder
   *  notification) can focus it once the queue has loaded. */
  registerAnswerButtonRef: (id: string, el: HTMLButtonElement | null) => void;
}

function EscalationRow({ item, onResolved, unseenCount, registerAnswerButtonRef }: EscalationRowProps) {
  const [action, setAction] = useState<'answer' | null>(null);

  const handleAnswered = () => {
    onResolved(item.id);
  };

  const ageBadgeVariant = getAgeBadgeVariant(item.createdAt);

  return (
    <div
      data-testid={`escalation-row-${item.id}`}
      className="rounded-lg border border-border bg-card p-4"
      role="listitem"
    >
      {/* Row header */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex flex-wrap items-center gap-2">
          <MessageCircleQuestion className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <span className="text-sm font-medium text-foreground">
            Escalation from{' '}
            <span
              data-testid={`escalation-row-agent-kind-${item.id}`}
              className="font-semibold"
            >
              {agentNameForSlug(item.agentKindSlug)}
            </span>
          </span>
          <Badge
            variant={ageBadgeVariant}
            data-testid={`escalation-row-age-${item.id}`}
            aria-label={`Waiting ${formatAge(item.createdAt)}`}
          >
            {formatAge(item.createdAt)}
          </Badge>
          {unseenCount > 0 && (
            <Badge
              variant="info"
              data-testid={`escalation-row-unseen-${item.id}`}
              aria-label={`${unseenCount} unseen update${unseenCount === 1 ? '' : 's'}`}
              className="gap-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" aria-hidden="true" />
              {unseenCount > 1 ? `${unseenCount > 99 ? '99+' : unseenCount} new` : 'New'}
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {item.leadId ? (
            <Link
              to={
                // BR-ESC-QUEUE-REACH: a reference check's work is on its lead's Verifications tab,
                // at that check's applicant (the case id is the reference check's id).
                item.agentKindSlug === 'reference_coordinator'
                  ? referenceCheckLeadPath(item.leadId, item.caseId)
                  : `/leasing/leads/${item.leadId}`
              }
              data-testid={`escalation-row-lead-link-${item.id}`}
              className="inline-flex min-h-[44px] items-center underline hover:text-foreground"
            >
              Open lead
            </Link>
          ) : item.repairRequestId ? (
            <Link
              to={`/repairs/${item.repairRequestId}`}
              data-testid={`escalation-row-repair-link-${item.id}`}
              className="inline-flex min-h-[44px] items-center underline hover:text-foreground"
            >
              Open repair
            </Link>
          ) : (
            <span data-testid={`escalation-row-case-${item.id}`}>
              Case escalation
            </span>
          )}
        </div>
      </div>

      {/* Reviewer blocks that raised this escalation (BR-QR-ESCALATE) */}
      {item.qualityReviewBlocks.map((block, n) => (
        <WhyAgentStoppedNote
          key={`${block.at}-${n}`}
          data-testid={`escalation-row-why-stopped-${item.id}-${n}`}
          critique={block.critique}
          tier={block.tier}
          itemText={block.itemText}
          blockedArgsSummary={block.blockedArgsSummary}
        />
      ))}

      {/* Escalated question */}
      <div
        data-testid={`escalation-row-question-${item.id}`}
        className="mb-4 p-3 rounded-md bg-muted/60 text-sm text-foreground whitespace-pre-wrap border border-border"
        aria-label="Agent-authored escalation question"
      >
        {item.question}
      </div>

      {/* Answer editor */}
      {action === 'answer' && (
        <AnswerEditor
          item={item}
          onResolved={handleAnswered}
          onCancel={() => setAction(null)}
        />
      )}

      {/* Action button (shown when not already answering) */}
      {action === null && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => setAction('answer')}
            ref={(el) => registerAnswerButtonRef(item.id, el)}
            data-testid={`escalation-row-answer-${item.id}`}
            aria-label="Answer this escalation"
          >
            Answer
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

export function AgentEscalationQueuePage() {
  const { data: featuresData } = useBillingFeatures();
  const hasAgenticSupport = featuresData?.features.includes('agentic_support') ?? false;

  const [items, setItems] = useState<EscalationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Set once a REAL fetch has completed — distinct from `loading`, which also goes
  // false on the very first render's !hasAgenticSupport branch below, before
  // useBillingFeatures() has even resolved (hasAgenticSupport defaults to false while
  // featuresData is still undefined). The deep-link effect gates on this, not on
  // `loading` alone, so it never mistakes that early false-negative window for "the
  // queue loaded and this escalation isn't in it".
  const queueLoadedRef = useRef(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    try {
      const data = await fetchEscalationQueue();
      setItems(data.escalations);
      queueLoadedRef.current = true;
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load escalation queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasAgenticSupport) {
      setLoading(false);
      return;
    }
    void loadQueue();
  }, [loadQueue, hasAgenticSupport]);

  // BR-7/US-40 — reactive on case.escalation_raised. No new arm was needed: the bridge
  // already forwards this event (its durable awareness write lives in
  // leasing-pipeline-notifier.ts, per BR-11a — the bridge still forwards it unchanged).
  useDomainEvents({
    'case.escalation_raised': useCallback(() => {
      void loadQueue();
    }, [loadQueue]),
  });

  // DC-4/BR-16 — per-row unseen marks, keyed by the lead or repair each escalation
  // belongs to (agent_landlord_escalation's declared referenceType is 'lead' for a
  // leasing case, 'repair_request' for a repair case — MF-BR-77). A case that is
  // neither (item.leadId and item.repairRequestId both null) never carries this
  // awareness class.
  const { data: unseenCountsData } = useUnseenCounts();
  const unseenByLeadId = new Map(
    (unseenCountsData?.byInstance ?? [])
      .filter((entry) => entry.referenceType === 'lead')
      .map((entry) => [entry.referenceId, entry.count])
  );
  const unseenByRepairId = new Map(
    (unseenCountsData?.byInstance ?? [])
      .filter((entry) => entry.referenceType === 'repair_request')
      .map((entry) => [entry.referenceId, entry.count])
  );

  const handleResolved = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    // Answering does not touch suspension state at all (BR-ESC-CONTINUE) — the agent
    // runtime wakes itself on the answer (or picks it up on hand-back if the landlord
    // took the conversation over directly). This queue is portfolio-level and does not
    // read per-case suspension state, so it says only what's certain either way.
    setSuccessMessage(
      'Answer recorded — the agent picks it up now, or when you hand the case back if you took over the conversation.'
    );
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // Deep-link focus (reminder notification): ?escalation=<id> scrolls that row into
  // view and focuses its Answer button once the queue has loaded; if it's no longer
  // open, a non-blocking status explains why instead of silently doing nothing.
  const [searchParams] = useSearchParams();
  const escalationParam = searchParams.get('escalation');
  const deepLinkHandledRef = useRef(false);
  const answerButtonRefs = useRef(new Map<string, HTMLButtonElement | null>());
  const [deepLinkNotFound, setDeepLinkNotFound] = useState(false);
  const registerAnswerButtonRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    answerButtonRefs.current.set(id, el);
  }, []);

  useEffect(() => {
    if (!escalationParam || loading || !queueLoadedRef.current || deepLinkHandledRef.current) return;
    deepLinkHandledRef.current = true;
    const match = items.find((item) => item.id === escalationParam);
    if (!match) {
      setDeepLinkNotFound(true);
      return;
    }
    const button = answerButtonRefs.current.get(escalationParam);
    button?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    button?.focus();
  }, [escalationParam, loading, items]);

  // Gate on agentic_support — featuresData is undefined while loading, so wait for
  // it before rendering (avoids flash of the not-enabled state).
  if (!featuresData) {
    return (
      <div data-testid="agent-escalation-queue-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-80 mt-2" />
        </div>
      </div>
    );
  }

  if (!hasAgenticSupport) {
    return (
      <div data-testid="agent-escalation-queue-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          <PageHeader
            title="Agent Escalation Queue"
            subtitle="Answer questions agents raise when they need your input"
            backLink={{ to: '/dashboard', label: 'Dashboard' }}
          />
        </div>
        <div className="px-[var(--page-padding-x)]">
          <AgentUpgradePrompt title="Turn on agent support to answer agent questions" />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div data-testid="agent-escalation-queue-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)] pt-4">
          <Skeleton className="h-6 w-56" />
          <Skeleton className="h-4 w-80 mt-2" />
        </div>
        <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="agent-escalation-queue-page" className="max-w-4xl mx-auto pb-6">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Agent Escalation Queue"
          subtitle="Answer questions agents raise when they need your input"
          backLink={{ to: '/dashboard', label: 'Dashboard' }}
        />
      </div>

      <div className="px-[var(--page-padding-x)] mb-4">
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          Answering does not pause the agent — it picks up your reply on its own, or when
          you hand the case back if you're handling it directly. Oldest escalations appear
          first.
        </div>
      </div>

      {/* Deep-link (reminder notification) target no longer open */}
      {deepLinkNotFound && (
        <div
          role="status"
          aria-live="polite"
          data-testid="agent-escalation-queue-deep-link-not-found"
          className="mx-[var(--page-padding-x)] mb-4 p-3 rounded-md border border-border bg-muted/40 text-muted-foreground text-sm"
        >
          That escalation is no longer open.
        </div>
      )}

      {/* Page error */}
      {pageError && (
        <div
          role="alert"
          data-testid="agent-escalation-queue-error"
          className="mx-[var(--page-padding-x)] mb-4 p-3 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm"
        >
          <p>{pageError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            data-testid="agent-escalation-queue-retry"
            onClick={() => void loadQueue()}
          >
            Retry
          </Button>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div
          role="status"
          aria-live="polite"
          data-testid="agent-escalation-queue-success"
          className="mx-[var(--page-padding-x)] mb-4 p-3 rounded-md border border-success/20 bg-success/10 text-success text-sm"
        >
          {successMessage}
        </div>
      )}

      {/* Empty state */}
      {!loading && items.length === 0 && !pageError && (
        <div data-testid="agent-escalation-queue-empty" className="px-[var(--page-padding-x)]">
          <EmptyState
            title="No open escalations"
            description="Questions agents raise when they need your input will appear here, oldest first."
            headingLevel="h2"
            action={
              <Button asChild variant="outline">
                <Link to="/dashboard">Back to Dashboard</Link>
              </Button>
            }
          />
        </div>
      )}

      {/* Queue items */}
      {items.length > 0 && (
        <div
          role="list"
          aria-label="Agent escalation queue"
          className="px-[var(--page-padding-x)] flex flex-col gap-4"
          data-testid="agent-escalation-queue-list"
        >
          {items.map((item) => (
            <EscalationRow
              key={item.id}
              item={item}
              onResolved={handleResolved}
              unseenCount={
                // A reference-check row carries its lead's id for navigation only; the lead's
                // unseen prospect messages are not about it.
                item.leadId && item.agentKindSlug !== 'reference_coordinator'
                  ? unseenByLeadId.get(item.leadId) ?? 0
                  : item.repairRequestId
                    ? unseenByRepairId.get(item.repairRequestId) ?? 0
                    : 0
              }
              registerAnswerButtonRef={registerAnswerButtonRef}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AgentEscalationQueuePage;
